import React, { useState, useEffect } from "react";
import "./meetingList.css";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Modal, Button } from "react-bootstrap";

const MeetingList = ({
  meetings,
  onEdit,
  onDelete,
  onRefresh,
  privileges,
  isProcessing,
}) => {
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    meeting: null,
  });
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const formatDateTime = (dateTimeStr) => {
    try {
      const date = new Date(dateTimeStr);
      return {
        date: date.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
        time: date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
      };
    } catch {
      return { date: "Invalid Date", time: "Invalid Time" };
    }
  };

  const getMeetingStatus = (startDateTime, durationMinutes) => {
    const now = currentTime;
    const meetingStart = new Date(startDateTime);
    const meetingEnd = new Date(
      meetingStart.getTime() + durationMinutes * 60 * 1000
    );
    const diffToStart = (meetingStart - now) / (1000 * 60);

    if (now > meetingEnd)
      return { status: "completed", label: "Completed", icon: "✓" };
    if (now >= meetingStart && now <= meetingEnd)
      return { status: "live", label: "Live Now", icon: "●" };
    if (diffToStart <= 15 && diffToStart > 0)
      return { status: "soon", label: "Starting Soon", icon: "⏰" };
    return { status: "scheduled", label: "Scheduled", icon: "📅" };
  };

  const handleJoinMeeting = (joinUrl) => {
    window.open(joinUrl, "_blank", "noopener,noreferrer");
  };

  const openDeleteModal = (meeting) => {
    setDeleteModal({ show: true, meeting });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ show: false, meeting: null });
  };

  const confirmDelete = async () => {
    if (deleteModal.meeting) {
      await onDelete?.(deleteModal.meeting.meeting_id);
      closeDeleteModal();
    }
  };

  if (meetings.length === 0) {
    const { edit: canEdit, delete: canDelete } = privileges || {};

    if (canEdit || canDelete) {
      return (
        <div className="meeting-list-container">
          <div className="empty-state">
            <div className="empty-icon">📹</div>
            <h2 className="empty-title">No meetings scheduled</h2>
            <p className="empty-subtitle">
              Schedule your first Zoom meeting to get started
            </p>
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="meeting-list-container">
      <div className="header">
        <div className="header-content">
          <h1 className="header-title">Your Meetings</h1>
          <p className="header-subtitle">
            {meetings.length} meeting{meetings.length !== 1 ? "s" : ""} found
          </p>
        </div>
        <button
          className="refresh-btn"
          onClick={onRefresh}
          disabled={isProcessing}
        >
          {isProcessing ? (
            <div className="spinner" />
          ) : (
            <RefreshIcon fontSize="small" />
          )}
          {isProcessing ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <div className="meetings-grid">
        {meetings.map((meeting) => {
          const dateTime = formatDateTime(meeting.startdatetime);
          const status = getMeetingStatus(
            meeting.startdatetime,
            meeting.duration
          );
          const {
            view: canJoin,
            edit: canEdit,
            delete: canDelete,
          } = privileges;

          return (
            <div
              key={meeting.meeting_id}
              className={`meeting-card ${
                status.status === "live" ? "live" : ""
              }`}
            >
              <div className="meeting-header">
                <h3 className="meeting-title">{meeting.topic}</h3>
                <div className={`status-badge status-${status.status}`}>
                  <span>{status.icon}</span>
                  {status.label}
                </div>
              </div>

              {meeting.description && (
                <p className="meeting-description">{meeting.description}</p>
              )}

              <div className="meeting-info">
                <div className="info-row">
                  <div className="info-icon schedule">📅</div>
                  <div className="info-text">
                    <p className="info-primary">{dateTime.date}</p>
                    <p className="info-secondary">
                      {dateTime.time} • {meeting.duration} min
                    </p>
                  </div>
                </div>

                {meeting.timezone && (
                  <div className="info-row">
                    <div className="info-icon group">🌍</div>
                    <div className="info-text">
                      <p className="info-primary">{meeting.timezone}</p>
                    </div>
                  </div>
                )}

                {meeting.zoommeetingid && (
                  <div className="meeting-id-box">
                    <p className="meeting-id-label">Meeting ID</p>
                    <p className="meeting-id-value">{meeting.zoommeetingid}</p>
                  </div>
                )}
              </div>

              <div className="meeting-actions">
                {canJoin && meeting.joinurl && (
                  <button
                    className={`join-btn ${
                      status.status === "live" ? "live" : "primary"
                    }`}
                    onClick={() => handleJoinMeeting(meeting.joinurl)}
                  >
                    <span>{status.status === "live" ? "▶️" : "📹"}</span>
                    {status.status === "live"
                      ? "Join Live Meeting"
                      : "Join Meeting"}
                  </button>
                )}

                {(canEdit || canDelete) && (
                  <div className="action-buttons">
                    {canEdit && (
                      <button
                        className="action-btn edit"
                        onClick={() => onEdit?.(meeting)}
                        disabled={isProcessing}
                      >
                        ✏️ Edit
                      </button>
                    )}
                    {canDelete && (
                      <button
                        className="action-btn delete"
                        onClick={() => openDeleteModal(meeting)}
                        disabled={isProcessing}
                      >
                        🗑️ Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* React Bootstrap Delete Modal */}
      <Modal 
        show={deleteModal.show} 
        onHide={closeDeleteModal}
        centered
        backdrop={isProcessing ? "static" : true}
        keyboard={!isProcessing}
      >
        <Modal.Header closeButton={!isProcessing}>
          <Modal.Title>
            <div className="d-flex align-items-center">
              <div 
                className="me-3 d-flex align-items-center justify-content-center"
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: 'rgba(220, 53, 69, 0.1)',
                  fontSize: '20px'
                }}
              >
                🗑️
              </div>
              Delete Meeting
            </div>
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body>
          <p className="mb-3">
            Are you sure you want to delete{" "}
            <strong>"{deleteModal.meeting?.topic}"</strong>?
          </p>

          <div 
            className="p-3 rounded"
            style={{
              backgroundColor: 'rgba(220, 53, 69, 0.05)',
              border: '1px solid rgba(220, 53, 69, 0.2)'
            }}
          >
            <p className="fw-bold text-danger mb-2" style={{ fontSize: '14px' }}>
              This action will:
            </p>
            <div style={{ fontSize: '14px', color: '#6c757d', lineHeight: '1.5' }}>
              • Cancel the Zoom meeting permanently<br />
              • Remove all associated data and recordings<br />
              • Cannot be undone
            </div>
          </div>
        </Modal.Body>
        
        <Modal.Footer>
          <Button 
            variant="outline-secondary" 
            onClick={closeDeleteModal}
            disabled={isProcessing}
          >
            Keep Meeting
          </Button>
          <Button 
            variant="danger" 
            onClick={confirmDelete}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Deleting...
              </>
            ) : (
              <>
                🗑️ Delete Meeting
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MeetingList;